import Button from '@/components/Button';
import Form from '@/components/Form';
import MultiOptionInput from '@/components/MultiOptionInput';
import useSession from '@/hooks/useSession';
import { ZitadelUser } from '@/models/sso';
import { Role, User } from '@/models/user';
import { AdminService } from '@/services/admin.service';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

interface UpdateFormProps {
  selectedUser: User | ZitadelUser;
  orgId: string;
  handleSubmit: () => void;
}

const UpdateForm: FC<UpdateFormProps> = ({
  handleSubmit,
  selectedUser,
  orgId,
}) => {
  const { session } = useSession();
  const isSsoUser = Object.prototype.hasOwnProperty.call(
    selectedUser,
    'zitadel_user_id'
  );

  // For legacy users, use Role enum; for SSO, fetch from backend
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [newRoles, setNewRoles] = useState<Role[]>(selectedUser.roles);
  const [isLoading, setIsLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(isSsoUser);

  // Current roles for comparison
  const currentRoles = selectedUser.roles || [];

  // Sync newRoles when the selected user changes
  useEffect(() => {
    setNewRoles(selectedUser.roles);
    setRolesLoading(isSsoUser);
  }, [isSsoUser, selectedUser?.roles]);

  // Fetch available roles from Zitadel for SSO users
  useEffect(() => {
    if (isSsoUser && orgId) {
      setRolesLoading(true);
      AdminService.getOrgAvailableRoles(orgId)
        .then((roles) => {
          setAvailableRoles(roles);
        })
        .catch((error) => {
          console.error('Failed to fetch available roles:', error);
          toast.error('Failed to load available roles');
          // Fallback to default roles
          setAvailableRoles([
            Role.USER,
            Role.ORG_USER_ADMIN,
            Role.ORG_DATA_ADMIN,
          ]);
        })
        .finally(() => {
          setRolesLoading(false);
        });
    } else {
      // For legacy users, use Role enum filtered by user permissions
      const roles = session.roles.includes(Role.SUPER_USER_ADMIN)
        ? Object.values(Role)
        : Object.values(Role).filter((role) => !role.startsWith('super-'));
      setAvailableRoles(roles);
    }
  }, [isSsoUser, orgId, session.roles]);

  const handleFormSubmit = async () => {
    setIsLoading(true);
    try {
      if (isSsoUser && orgId) {
        // SSO user: call updateSsoUserRoles
        await AdminService.updateSsoUserRoles(
          orgId,
          (selectedUser as ZitadelUser).zitadel_user_id,
          newRoles
        );
      } else if (selectedUser) {
        // Legacy user: call updateRoles
        const res = await AdminService.updateRoles(
          (selectedUser as User).id,
          newRoles as Role[]
        );
        if (!res) {
          throw new Error('Failed to update user roles');
        }
      } else {
        // No user selected - this should not happen but guard against it
        throw new Error('No user selected for role update');
      }
      handleSubmit();
      toast.success('User roles updated successfully.');
    } catch (error) {
      console.error('Error updating user roles:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = (
    _name: string,
    value: unknown,
    selected: boolean
  ) => {
    if (value === Role.USER && !selected) {
      return;
    }
    if (selected) {
      setNewRoles((prev) => [...prev, value as Role]);
    } else {
      setNewRoles((prev) => prev.filter((role) => role !== value));
    }
  };

  // Check if roles have changed
  const hasChanges =
    newRoles.length !== currentRoles.length ||
    !newRoles.every((role) => currentRoles.includes(role));

  if (rolesLoading) {
    return <section>Loading roles...</section>;
  }

  return (
    <section>
      <Form onSubmit={handleFormSubmit}>
        <MultiOptionInput
          options={availableRoles}
          selectedOptions={newRoles}
          name="roles"
          handleChange={handleRoleChange}
          variation="filters"
        />
        <Button
          variation="alt"
          loading={isLoading}
          disabled={newRoles.length === 0 || !hasChanges}
        >
          Save
        </Button>
      </Form>
    </section>
  );
};

export default UpdateForm;
